import React, { useState, useMemo, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Form, Modal, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faWeightHanging, 
  faTint, 
  faCube, 
  faCubes, 
  faIndustry, 
  faMountain,
  faFlask,
  faArrowLeft,
  faChartLine,
  faEye,
  faEdit,
  faTrash,
  faSeedling,
  faFilter,
  faSearch,
  faHammer
} from '@fortawesome/free-solid-svg-icons';
import { getAllLiquidAdmixtureTests } from '../apis/liquidAdmixture';
import { getAllCoarseAggregateTestRequests, deleteCoarseAggregateTestRequest } from '../apis/coarseAggregate';
import { getAllCementTestingTestRequests, deleteCementTestingTestRequest } from '../apis/cementTesting';
import { getAllBulkDensityTestRequests, deleteBulkDensityTestRequest } from '../apis/bulkDensity';
import { getAllFineAggregateTestRequests, deleteFineAggregateTestRequest, getFineAggregateTestRequest } from '../apis/fineAggregate';
import { getAllAacBlockTestRequests, deleteAacBlockTestRequest } from '../apis/aacBlock';
import { getAllSteelTests } from '../apis/steelTesting';

// Service definitions
const services = [
  { id: 'all', name: 'All Services', icon: faFlask, color: 'primary' },
  { id: 'bulk-density', name: 'Bulk Density and Moisture Content', icon: faWeightHanging, color: 'success' },
  { id: 'liquid-admixture', name: 'Liquid Admixture', icon: faTint, color: 'danger' },
  { id: 'steel-testing', name: 'Steel Testing', icon: faHammer, color: 'dark' },
  { id: 'aac-blocks', name: 'AAC Blocks', icon: faCube, color: 'warning' },
  { id: 'coarse-aggregate', name: 'Coarse Aggregate Testing', icon: faCubes, color: 'primary' },
  { id: 'cement-testing', name: 'Cement Testing', icon: faIndustry, color: 'secondary' },
  { id: 'fine-aggregate', name: 'Fine Aggregate', icon: faMountain, color: 'info' },
  { id: 'soil-testing', name: 'Soil Testing', icon: faSeedling, color: 'brown' }
];

const OtherServicesDashboard = () => {
  const [selectedService, setSelectedService] = useState('all');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [allTestData, setAllTestData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [testToDelete, setTestToDelete] = useState(null);
  const navigate = useNavigate();

  // Fetch all test data from backend (liquid admixture + coarse aggregate)
  useEffect(() => {
    const fetchAllTestData = async () => {
      try {
        setLoading(true);
        console.log('🔵 [DASHBOARD] Fetching all test data in parallel...');
        
        // Fetch all test data in parallel for faster loading
        const [
          liquidTests,
          coarseAggregateTests,
          cementTests,
          bulkDensityTests,
          fineAggregateTests,
          aacBlockTests,
          steelTests
        ] = await Promise.all([
          getAllLiquidAdmixtureTests().catch(err => {
            console.error('❌ [DASHBOARD] Error fetching liquid admixture tests:', err);
            return [];
          }),
          getAllCoarseAggregateTestRequests().catch(err => {
            console.error('❌ [DASHBOARD] Error fetching coarse aggregate tests:', err);
            return [];
          }),
          getAllCementTestingTestRequests().catch(err => {
            console.error('❌ [DASHBOARD] Error fetching cement tests:', err);
            return [];
          }),
          getAllBulkDensityTestRequests().catch(err => {
            console.error('❌ [DASHBOARD] Error fetching bulk density tests:', err);
            return [];
          }),
          getAllFineAggregateTestRequests().catch(err => {
            console.error('❌ [DASHBOARD] Error fetching fine aggregate tests:', err);
            return [];
          }),
          getAllAacBlockTestRequests().catch(err => {
            console.error('❌ [DASHBOARD] Error fetching AAC Block tests:', err);
            return [];
          }),
          getAllSteelTests().catch(err => {
            console.error('❌ [DASHBOARD] Error fetching steel tests:', err);
            return [];
          })
        ]);
        
        console.log('✅ [DASHBOARD] All test data fetched successfully');
        console.log('   - Liquid Admixture:', liquidTests.length);
        console.log('   - Coarse Aggregate:', coarseAggregateTests.length);
        console.log('   - Cement Testing:', cementTests.length);
        console.log('   - Bulk Density:', bulkDensityTests.length);
        console.log('   - Fine Aggregate:', fineAggregateTests.length);
        console.log('   - AAC Blocks:', aacBlockTests.length);
        console.log('   - Steel Testing:', steelTests.length);
        
        // Convert liquid admixture to dashboard format (existing format - unchanged)
        const formattedLiquidTests = liquidTests.map(test => ({
          id: test.id,
          uniqueKey: `liquid-admixture-${test.id}`,
          serviceType: 'liquid-admixture',
          serviceName: 'Liquid Admixture',
          customer: test.customer_name || test.customer || 'N/A',
          jobNumber: test.job_code_number || test.jobNumber || 'N/A',
          site: test.site_name || test.site || 'N/A',
          status: test.status || 'pending',
          date: test.date_of_receipt || test.date || test.created_at || new Date().toISOString(),
          reportDate: test.report_date || test.date_of_report || null,
          report_date: test.report_date || test.date_of_report || null,
          icon: faTint,
          color: 'danger',
          testId: test.id
        }));
        
        // Convert coarse aggregate to dashboard format (new format)
        const formattedCoarseAggregateTests = coarseAggregateTests.map(test => ({
          id: test.id,
          uniqueKey: `coarse-aggregate-${test.id}`,
          serviceType: 'coarse-aggregate',
          serviceName: 'Coarse Aggregate Testing',
          customer: test.customer_name || 'N/A',
          jobNumber: test.job_number || 'N/A',
          site: test.site_name || 'N/A',
          status: test.status || 'draft',
          date: test.receipt_date || test.created_at || new Date().toISOString(),
          reportDate: test.report_date || test.date_of_report || null,
          report_date: test.report_date || test.date_of_report || null,
          icon: faCubes,
          color: 'primary',
          testId: test.id
        }));
        
        // Convert cement testing to dashboard format (new format)
        const formattedCementTests = cementTests.map(test => ({
          id: test.id,
          uniqueKey: `cement-testing-${test.id}`,
          serviceType: 'cement-testing',
          serviceName: 'Cement Testing',
          customer: test.customer_name || 'N/A',
          jobNumber: test.job_code_number || test.job_number || 'N/A',
          site: test.site_name || test.site_address || 'N/A',
          status: test.status || 'draft',
          date: test.date_of_receipt || test.receipt_date || test.created_at || new Date().toISOString(),
          reportDate: test.report_date || test.date_of_report || null,
          report_date: test.report_date || test.date_of_report || null,
          icon: faIndustry,
          color: 'secondary',
          testId: test.id
        }));
        
        // Convert bulk density to dashboard format (new format)
        const formattedBulkDensityTests = bulkDensityTests.map(test => ({
          id: test.id,
          uniqueKey: `bulk-density-${test.id}`,
          serviceType: 'bulk-density',
          serviceName: 'Bulk Density and Moisture Content',
          customer: test.customer_name || 'N/A',
          jobNumber: test.job_code_number || test.job_number || 'N/A',
          site: test.site_name || 'N/A',
          status: test.status || 'pending',
          date: test.date_of_receipt || test.date_of_testing || test.created_at || new Date().toISOString(),
          reportDate: test.report_date || test.date_of_report || null,
          report_date: test.report_date || test.date_of_report || null,
          icon: faWeightHanging,
          color: 'success',
          testId: test.id
        }));
        
        // Convert fine aggregate to dashboard format (new format)
        const formattedFineAggregateTests = fineAggregateTests.map(test => ({
          id: test.id,
          uniqueKey: `fine-aggregate-${test.id}`,
          serviceType: 'fine-aggregate',
          serviceName: 'Fine Aggregate',
          customer: test.customer_name || 'N/A',
          jobNumber: test.job_code_number || 'N/A',
          site: test.site_name || 'N/A',
          status: test.status || 'draft',
          date: test.date_of_receipt || test.created_at || new Date().toISOString(),
          reportDate: test.report_date || test.date_of_report || null,
          report_date: test.report_date || test.date_of_report || null,
          icon: faMountain,
          color: 'info',
          testId: test.id
        }));
        
        // Convert AAC Block to dashboard format (normalize status for stats: draft -> pending)
        const formattedAacBlockTests = aacBlockTests.map(test => ({
          id: test.id,
          uniqueKey: `aac-blocks-${test.id}`,
          serviceType: 'aac-blocks',
          serviceName: 'AAC Blocks',
          customer: test.customer_name || 'N/A',
          jobNumber: test.job_number || 'N/A',
          site: test.site_name || 'N/A',
          status: (test.status === 'completed' ? 'completed' : test.status === 'in-progress' ? 'in-progress' : 'pending') || 'pending',
          date: test.receipt_date || test.created_at || new Date().toISOString(),
          reportDate: null,
          report_date: null,
          icon: faCube,
          color: 'warning',
          testId: test.id
        }));

        // Convert Steel Testing to dashboard format (saved tests are treated as completed)
        const formattedSteelTests = steelTests.map(test => ({
          id: test.id,
          uniqueKey: `steel-testing-${test.id}`,
          serviceType: 'steel-testing',
          serviceName: 'Steel Testing',
          customer: test.customer_name || 'N/A',
          jobNumber: test.job_code_number || 'N/A',
          site: test.site_name || 'N/A',
          status: 'completed',
          date: test.date_of_receipt || test.created_at || new Date().toISOString(),
          reportDate: test.date_of_report || null,
          report_date: test.date_of_report || null,
          icon: faHammer,
          color: 'dark',
          testId: test.id
        }));
        
        // Combine all test data
        const allFormattedTests = [...formattedLiquidTests, ...formattedCoarseAggregateTests, ...formattedCementTests, ...formattedBulkDensityTests, ...formattedFineAggregateTests, ...formattedAacBlockTests, ...formattedSteelTests];
        
        setAllTestData(allFormattedTests);
        console.log('✅ [DASHBOARD] Loaded', allFormattedTests.length, 'tests from API');
      } catch (error) {
        console.error('❌ [DASHBOARD] Error fetching test data:', error);
        // On error, set empty array (no mock data)
        setAllTestData([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllTestData();
  }, []);

  // Filter data based on selected service, status, and search query
  const filteredData = useMemo(() => {
    let filtered = allTestData;

    // Filter by service
    if (selectedService !== 'all') {
      filtered = filtered.filter(item => item.serviceType === selectedService);
    }

    // Filter by status
    if (selectedFilter !== 'all') {
      filtered = filtered.filter(item => item.status === selectedFilter);
    }

    // Filter by search query (case-insensitive search across multiple fields)
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(item => {
        const serviceName = (item.serviceName || '').toLowerCase();
        const jobNumber = (item.jobNumber || '').toLowerCase();
        const customer = (item.customer || '').toLowerCase();
        const site = (item.site || '').toLowerCase();
        
        return serviceName.includes(query) ||
               jobNumber.includes(query) ||
               customer.includes(query) ||
               site.includes(query);
      });
    }

    return filtered;
  }, [selectedService, selectedFilter, searchQuery, allTestData]);

  // Calculate statistics based on filtered data
  const stats = useMemo(() => {
    let dataToCalculate = allTestData;
    
    if (selectedService !== 'all') {
      dataToCalculate = allTestData.filter(item => item.serviceType === selectedService);
    }

    return {
      total: dataToCalculate.length,
      completed: dataToCalculate.filter(item => item.status === 'completed').length,
      inProgress: dataToCalculate.filter(item => item.status === 'in-progress').length,
      pending: dataToCalculate.filter(item => item.status === 'pending').length
    };
  }, [selectedService, allTestData]);

  // Handle view action
  const handleView = async (test) => {
    if (test.serviceType === 'liquid-admixture' && test.testId) {
      // Navigate to liquid admixture form with test ID
      navigate(`/liquid-admixture-form?testId=${test.testId}`);
    } else if (test.serviceType === 'coarse-aggregate' && test.testId) {
      // Navigate to coarse aggregate form with test request ID
      navigate(`/coarse-aggregate-form?testRequestId=${test.testId}`);
    } else if (test.serviceType === 'cement-testing' && test.testId) {
      // Navigate to cement testing form with test request ID
      navigate(`/cement-testing-form?cementTestRequestId=${test.testId}`);
    } else if (test.serviceType === 'aac-blocks' && test.testId) {
      // Navigate to AAC Block form with test request ID (view saved test)
      navigate(`/aac-blocks-form?testRequestId=${test.testId}`);
    } else if (test.serviceType === 'steel-testing' && test.testId) {
      navigate(`/steel-testing-form?id=${test.testId}`);
    } else if (test.serviceType === 'fine-aggregate' && test.testId) {
      // Fetch test data and navigate to test results page
      try {
        const testData = await getFineAggregateTestRequest(test.testId);
        console.log('✅ Fetched Fine Aggregate test data:', testData);
        
        // HTTP-based debug logging
        fetch('http://127.0.0.1:7242/ingest/30abe990-36a0-4ac0-8ff5-37cb3c3d7fe3', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            location: 'OtherServicesDashboard.js:handleView',
            message: 'Fetched Fine Aggregate test data from API',
            data: {
              testId: test.testId,
              hasSieveAnalysis: !!testData.sieve_analysis,
              sieveAnalysisKeys: testData.sieve_analysis ? Object.keys(testData.sieve_analysis) : [],
              selectedTests: testData.selected_tests || []
            },
            timestamp: Date.now(),
            sessionId: 'debug-session',
            runId: 'run1',
            hypothesisId: 'B'
          })
        }).catch(() => {});
        
        // Extract selected tests and form data
        const selectedTests = testData.selected_tests || [];
        const testReport = testData.test_report || {};
        const testVerification = testData.test_verification || {};
        const bulkDensity = testData.bulk_density || {};
        const sieveAnalysis = testData.sieve_analysis || {};
        const specificGravity = testData.specific_gravity || {};
        
        // Build formData by merging all data sources
        const formData = {
          // Customer info
          customer_id: testData.customer_id,
          customer_name: testData.customer_name,
          
          // Test report data
          ...testReport,
          
          // Map date fields (handle both formats)
          date_of_receipt: testReport.date_of_material_receipt || testReport.date_of_receipt,
          date_of_testing: bulkDensity.date_of_testing || sieveAnalysis.date_of_testing || specificGravity.date_of_testing,
          
          // Verification data
          ...testVerification,
          
          // Bulk Density test data
          ...bulkDensity,
          
          // Sieve Analysis test data - ensure all fields are included
          ...(sieveAnalysis || {}),
          
          // Specific Gravity test data
          ...specificGravity,
          
          // URL number mapping
          url_number: testReport.ulr_number || testReport.url_number
        };
        
        console.log('📦 Prepared formData for navigation:', formData);
        console.log('📋 Selected tests:', selectedTests);
        
        // Debug: Check sieve analysis data specifically
        if (selectedTests.includes('sieveAnalysis')) {
          console.log('🔍 [DASHBOARD] Raw sieveAnalysis object:', sieveAnalysis);
          console.log('🔍 [DASHBOARD] Sieve Analysis keys in raw object:', Object.keys(sieveAnalysis || {}));
          console.log('🔍 [DASHBOARD] Sieve Analysis data in formData:', {
            total_weight: formData.total_weight,
            sieve_weight_retained_01: formData.sieve_weight_retained_01,
            sieve_weight_retained_02: formData.sieve_weight_retained_02,
            sieve_percent_weight_retained_01: formData.sieve_percent_weight_retained_01,
            sieve_cumulative_percent_retained_01: formData.sieve_cumulative_percent_retained_01,
            sieve_percent_passing_01: formData.sieve_percent_passing_01,
            fineness_modulus: formData.fineness_modulus,
            zone_result: formData.zone_result,
            sieve_sum_cumulative_excluding_pan: formData.sieve_sum_cumulative_excluding_pan
          });
          
          // Check if any sieve fields exist in formData
          const sieveFieldsInFormData = Object.keys(formData).filter(k => k.includes('sieve') || k === 'total_weight' || k === 'fineness_modulus' || k === 'zone_result');
          console.log('🔍 [DASHBOARD] All sieve-related fields in formData:', sieveFieldsInFormData);
          const sieveFieldValues = {};
          sieveFieldsInFormData.forEach(field => {
            console.log(`   ${field}: ${formData[field]} (${typeof formData[field]})`);
            sieveFieldValues[field] = { value: formData[field], type: typeof formData[field] };
          });
          
          // HTTP-based debug logging
          fetch('http://127.0.0.1:7242/ingest/30abe990-36a0-4ac0-8ff5-37cb3c3d7fe3', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              location: 'OtherServicesDashboard.js:handleView',
              message: 'Sieve Analysis fields in formData before navigation',
              data: { sieveFieldsInFormData, sieveFieldValues, rawSieveAnalysis: sieveAnalysis },
              timestamp: Date.now(),
              sessionId: 'debug-session',
              runId: 'run1',
              hypothesisId: 'C'
            })
          }).catch(() => {});
        }
        
        // Navigate to test results page with all data
        navigate('/test-results', {
          state: {
            module: 'fineAggregate',
            selectedTests: selectedTests,
            formData: formData,
            testRequestId: test.testId
          }
        });
      } catch (error) {
        console.error('❌ Error fetching Fine Aggregate test data:', error);
        alert('Failed to load test data. Please try again.');
      }
    }
  };

  // Handle edit action
  const handleEdit = (test) => {
    if (test.serviceType === 'liquid-admixture' && test.testId) {
      // Navigate to liquid admixture form with test ID for editing
      navigate(`/liquid-admixture-form?testId=${test.testId}&edit=true`);
    } else if (test.serviceType === 'coarse-aggregate' && test.testId) {
      // Navigate to coarse aggregate form with test request ID for editing
      navigate(`/coarse-aggregate-form?testRequestId=${test.testId}&edit=true`);
    } else if (test.serviceType === 'cement-testing' && test.testId) {
      // Navigate to cement testing form with test request ID for editing
      navigate(`/cement-testing-form?cementTestRequestId=${test.testId}&edit=true`);
    } else if (test.serviceType === 'aac-blocks' && test.testId) {
      // Navigate to AAC Block form with test request ID for editing
      navigate(`/aac-blocks-form?testRequestId=${test.testId}&edit=true`);
    } else if (test.serviceType === 'fine-aggregate' && test.testId) {
      // Navigate to fine aggregate form with test request ID for editing
      navigate(`/fine-aggregate-form?testRequestId=${test.testId}&edit=true`);
    } else if (test.serviceType === 'steel-testing' && test.testId) {
      navigate(`/steel-testing-form?id=${test.testId}`);
    }
  };

  // Handle delete action - show confirmation modal
  const handleDelete = (test) => {
    setTestToDelete(test);
    setShowDeleteModal(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!testToDelete) return;

    setShowDeleteModal(false);

    try {
      if (testToDelete.serviceType === 'coarse-aggregate' && testToDelete.testId) {
        await deleteCoarseAggregateTestRequest(testToDelete.testId);
        console.log('✅ [DASHBOARD] Deleted coarse aggregate test request:', testToDelete.testId);
        
        // Remove from local state
        setAllTestData(prev => prev.filter(item => item.id !== testToDelete.id));
        
        // Show success message
        alert('Test request deleted successfully');
      } else if (testToDelete.serviceType === 'liquid-admixture' && testToDelete.testId) {
        // TODO: Add liquid admixture delete if needed
        alert('Delete functionality for liquid admixture is not yet implemented');
      } else if (testToDelete.serviceType === 'fine-aggregate' && testToDelete.testId) {
        await deleteFineAggregateTestRequest(testToDelete.testId);
        console.log('✅ [DASHBOARD] Deleted fine aggregate test request:', testToDelete.testId);
        
        // Remove from local state
        setAllTestData(prev => prev.filter(item => item.id !== testToDelete.id));
        
        // Show success message
        alert('Test request deleted successfully');
      } else if (testToDelete.serviceType === 'aac-blocks' && testToDelete.testId) {
        await deleteAacBlockTestRequest(testToDelete.testId);
        console.log('✅ [DASHBOARD] Deleted AAC Block test request:', testToDelete.testId);
        
        // Remove from local state
        setAllTestData(prev => prev.filter(item => item.id !== testToDelete.id));
        
        // Show success message
        alert('Test request deleted successfully');
      } else if (testToDelete.serviceType === 'steel-testing' && testToDelete.testId) {
        alert('Delete functionality for Steel Testing is not yet implemented');
      }
    } catch (error) {
      console.error('❌ [DASHBOARD] Error deleting test:', error);
      alert('Failed to delete test. Please try again.');
    } finally {
      setTestToDelete(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge bg="success">Completed</Badge>;
      case 'in-progress':
        return <Badge bg="warning">In Progress</Badge>;
      case 'pending':
        return <Badge bg="info">Pending</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  // Get selected service details
  const selectedServiceDetails = services.find(s => s.id === selectedService) || services[0];

  // Loading overlay component
  if (loading) {
    return (
      <div>
        {/* Header still visible during loading */}
        <div className="header-vitrag">
          <Container>
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faChartLine} className="me-3" style={{ fontSize: '2rem' }} />
                  <h1 style={{ color: '#ffffff', fontWeight: '700', margin: 0 }}>
                    Other Services Dashboard
                  </h1>
                </div>
              </Col>
              <Col md={4} className="text-end">
                <Button 
                  as={Link}
                  to="/other-services"
                  variant="outline-light"
                  className="btn-vitrag-primary"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Back to Services
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
        
        {/* Full-screen loading overlay */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(5px)'
        }}>
          <Spinner 
            animation="border" 
            variant="warning" 
            style={{ 
              width: '4rem', 
              height: '4rem',
              borderWidth: '0.3rem',
              marginBottom: '1.5rem'
            }} 
          />
          <h2 style={{ 
            color: 'var(--vitrag-gold)', 
            fontWeight: '600',
            marginBottom: '0.5rem',
            fontSize: '1.75rem'
          }}>
            Loading Dashboard...
          </h2>
          <p style={{ 
            color: '#e2e8f0', 
            fontSize: '1rem',
            opacity: 0.8
          }}>
            Fetching test data from all services
          </p>
          <div style={{
            marginTop: '2rem',
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            color: '#9ca3af',
            fontSize: '0.875rem'
          }}>
            <FontAwesomeIcon icon={faFlask} className="me-1" />
            <span>Loading test data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="header-vitrag">
        <Container>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faChartLine} className="me-3" style={{ fontSize: '2rem' }} />
                <h1 style={{ color: '#ffffff', fontWeight: '700', margin: 0 }}>
                  Other Services Dashboard
                </h1>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <Button 
                as={Link}
                to="/other-services"
                variant="outline-light"
                className="btn-vitrag-primary"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Services
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-4">
        {/* Service Selector */}
        <Row className="mb-4">
          <Col>
            <Card className="card-vitrag shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center flex-wrap gap-3">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faFilter} className="text-warning me-2" size="lg" />
                    <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                      Filter by Service:
                    </h5>
                  </div>
                  <div className="flex-grow-1" style={{ minWidth: '250px', maxWidth: '400px' }}>
                    <Form.Select
                      value={selectedService}
                      onChange={(e) => setSelectedService(e.target.value)}
                      style={{
                        backgroundColor: '#2d3748',
                        border: '2px solid rgba(255, 165, 0, 0.3)',
                        borderRadius: '10px',
                        color: '#e2e8f0',
                        padding: '12px 15px',
                        fontSize: '1rem',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        cursor: 'pointer'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#FFA500';
                        e.target.style.boxShadow = '0 0 0 3px rgba(255, 165, 0, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(255, 165, 0, 0.3)';
                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                      }}
                    >
                      {services.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  {selectedService !== 'all' && (
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon 
                        icon={selectedServiceDetails.icon} 
                        className={`text-${selectedServiceDetails.color} me-2`}
                      />
                      <span className="text-muted">
                        {filteredData.length} test{filteredData.length !== 1 ? 's' : ''} found
                      </span>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Statistics Cards */}
        <Row className="mb-4">
          <Col lg={3} md={6} className="mb-3">
            <Card className="card-vitrag shadow-sm text-center" style={{
              background: 'linear-gradient(135deg, var(--vitrag-card-bg) 0%, #2a3441 100%)',
              border: '1px solid rgba(255, 215, 0, 0.2)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
            }}
            >
              <Card.Body className="p-4">
                <FontAwesomeIcon icon={faFlask} className="text-primary mb-2" size="2x" />
                <h3 className="text-primary mb-2" style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: '800',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}>
                  {stats.total}
                </h3>
                <p className="text-muted mb-0" style={{ fontSize: '1rem', fontWeight: '500' }}>
                  Total Tests
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="card-vitrag shadow-sm text-center" style={{
              background: 'linear-gradient(135deg, var(--vitrag-card-bg) 0%, #2a3441 100%)',
              border: '1px solid rgba(40, 167, 69, 0.2)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
            }}
            >
              <Card.Body className="p-4">
                <FontAwesomeIcon icon={faWeightHanging} className="text-success mb-2" size="2x" />
                <h3 className="text-success mb-2" style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: '800',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}>
                  {stats.completed}
                </h3>
                <p className="text-muted mb-0" style={{ fontSize: '1rem', fontWeight: '500' }}>
                  Completed
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="card-vitrag shadow-sm text-center" style={{
              background: 'linear-gradient(135deg, var(--vitrag-card-bg) 0%, #2a3441 100%)',
              border: '1px solid rgba(255, 193, 7, 0.2)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
            }}
            >
              <Card.Body className="p-4">
                <FontAwesomeIcon icon={faTint} className="text-warning mb-2" size="2x" />
                <h3 className="text-warning mb-2" style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: '800',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}>
                  {stats.inProgress}
                </h3>
                <p className="text-muted mb-0" style={{ fontSize: '1rem', fontWeight: '500' }}>
                  In Progress
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="card-vitrag shadow-sm text-center" style={{
              background: 'linear-gradient(135deg, var(--vitrag-card-bg) 0%, #2a3441 100%)',
              border: '1px solid rgba(23, 162, 184, 0.2)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
            }}
            >
              <Card.Body className="p-4">
                <FontAwesomeIcon icon={faCube} className="text-info mb-2" size="2x" />
                <h3 className="text-info mb-2" style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: '800',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}>
                  {stats.pending}
                </h3>
                <p className="text-muted mb-0" style={{ fontSize: '1rem', fontWeight: '500' }}>
                  Pending
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Search Bar */}
        <Row className="mb-4">
          <Col>
            <Card className="card-vitrag shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center gap-3">
                  <FontAwesomeIcon icon={faSearch} className="text-warning" size="lg" />
                  <Form.Control
                    type="text"
                    placeholder="Search by Service Type, Job Number, Customer, or Site..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      backgroundColor: '#2d3748',
                      border: '2px solid rgba(255, 165, 0, 0.3)',
                      borderRadius: '10px',
                      color: '#e2e8f0',
                      padding: '12px 15px',
                      fontSize: '1rem'
                    }}
                  />
                  {searchQuery && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setSearchQuery('')}
                      style={{ borderRadius: '8px', padding: '6px 12px' }}
                      title="Clear search"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filter Buttons */}
        <Row className="mb-4">
          <Col>
            <Card className="card-vitrag shadow-sm">
              <Card.Body>
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  <span className="text-muted me-2" style={{ fontWeight: '500' }}>Filter by Status:</span>
                  <Button
                    variant={selectedFilter === 'all' ? 'primary' : 'outline-primary'}
                    onClick={() => setSelectedFilter('all')}
                    size="sm"
                    style={{ borderRadius: '8px', padding: '6px 16px' }}
                  >
                    All Tests
                  </Button>
                  <Button
                    variant={selectedFilter === 'completed' ? 'success' : 'outline-success'}
                    onClick={() => setSelectedFilter('completed')}
                    size="sm"
                    style={{ borderRadius: '8px', padding: '6px 16px' }}
                  >
                    Completed
                  </Button>
                  <Button
                    variant={selectedFilter === 'in-progress' ? 'warning' : 'outline-warning'}
                    onClick={() => setSelectedFilter('in-progress')}
                    size="sm"
                    style={{ borderRadius: '8px', padding: '6px 16px' }}
                  >
                    In Progress
                  </Button>
                  <Button
                    variant={selectedFilter === 'pending' ? 'info' : 'outline-info'}
                    onClick={() => setSelectedFilter('pending')}
                    size="sm"
                    style={{ borderRadius: '8px', padding: '6px 16px' }}
                  >
                    Pending
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Test Results Table */}
        <Row>
          <Col>
            <Card className="card-vitrag shadow-sm">
              <Card.Header style={{ 
                backgroundColor: 'var(--vitrag-secondary)', 
                borderBottom: '2px solid rgba(255, 165, 0, 0.3)'
              }}>
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faFlask} className="me-2" />
                    Test Results Overview
                  </h5>
                  <div className="d-flex align-items-center gap-3">
                    <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                      Showing {filteredData.length} of {allTestData.length} tests
                      {searchQuery && (
                        <span className="text-warning ms-2" style={{ fontSize: '0.85rem' }}>
                          (Filtered by: "{searchQuery}")
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </Card.Header>
              <Card.Body style={{ padding: 0 }}>
                {filteredData.length === 0 ? (
                  <div className="text-center py-5">
                    <FontAwesomeIcon icon={faFlask} className="text-muted mb-3" size="3x" />
                    <p className="text-muted mb-0" style={{ fontSize: '1.1rem' }}>
                      No test data found for the selected filters.
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="table-dark" style={{ backgroundColor: '#1C2333' }}>
                        <tr>
                          <th style={{ padding: '15px', borderBottom: '2px solid rgba(255, 165, 0, 0.3)' }}>
                            Service Type
                          </th>
                          <th style={{ padding: '15px', borderBottom: '2px solid rgba(255, 165, 0, 0.3)' }}>
                            Job Number
                          </th>
                          <th style={{ padding: '15px', borderBottom: '2px solid rgba(255, 165, 0, 0.3)' }}>
                            Customer
                          </th>
                          <th style={{ padding: '15px', borderBottom: '2px solid rgba(255, 165, 0, 0.3)' }}>
                            Site
                          </th>
                          <th style={{ padding: '15px', borderBottom: '2px solid rgba(255, 165, 0, 0.3)' }}>
                            Report Date
                          </th>
                          <th style={{ padding: '15px', borderBottom: '2px solid rgba(255, 165, 0, 0.3)', textAlign: 'center' }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.map((test) => (
                          <tr 
                            key={test.uniqueKey || `${test.serviceType}-${test.id}`}
                            style={{ 
                              transition: 'background-color 0.2s ease',
                              borderBottom: '1px solid rgba(255, 165, 0, 0.1)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 165, 0, 0.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <td style={{ padding: '15px', verticalAlign: 'middle' }}>
                              <div className="d-flex align-items-center">
                                <FontAwesomeIcon 
                                  icon={test.icon} 
                                  className={`text-${test.color} me-2`}
                                  style={{ fontSize: '1.2rem' }}
                                />
                                <span style={{ fontWeight: '500' }}>{test.serviceName}</span>
                              </div>
                            </td>
                            <td style={{ padding: '15px', verticalAlign: 'middle' }}>
                              <span style={{ fontFamily: 'monospace', color: 'var(--vitrag-gold)' }}>
                                {test.jobNumber}
                              </span>
                            </td>
                            <td style={{ padding: '15px', verticalAlign: 'middle' }}>
                              {test.customer}
                            </td>
                            <td style={{ padding: '15px', verticalAlign: 'middle' }}>
                              {test.site || 'N/A'}
                            </td>
                            <td style={{ padding: '15px', verticalAlign: 'middle' }}>
                              {test.reportDate || test.report_date || test.date
                                ? new Date(test.reportDate || test.report_date || test.date).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })
                                : 'N/A'}
                            </td>
                            <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center' }}>
                              <div className="d-flex gap-2 justify-content-center">
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  title="View Details"
                                  style={{ borderRadius: '6px' }}
                                  onClick={() => handleView(test)}
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                </Button>
                                <Button 
                                  variant="outline-warning" 
                                  size="sm"
                                  title="Edit"
                                  style={{ borderRadius: '6px' }}
                                  onClick={() => handleEdit(test)}
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </Button>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm"
                                  title="Delete"
                                  style={{ borderRadius: '6px' }}
                                  onClick={() => handleDelete(test)}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Custom Delete Confirmation Modal */}
      <Modal 
        show={showDeleteModal} 
        onHide={() => {
          setShowDeleteModal(false);
          setTestToDelete(null);
        }} 
        centered
        backdrop="static"
        contentClassName="custom-delete-modal"
      >
        <style>{`
          .custom-delete-modal .modal-content {
            background-color: #2c2c2c;
            border: none;
            border-radius: 12px;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
            overflow: hidden;
          }
          .custom-delete-modal .modal-header {
            background-color: #2c2c2c;
            border-bottom: 1px solid #444;
            padding: 16px 24px;
            border-radius: 12px 12px 0 0;
          }
          .custom-delete-modal .modal-header .btn-close {
            filter: invert(1);
            opacity: 0.8;
          }
          .custom-delete-modal .modal-header .btn-close:hover {
            opacity: 1;
          }
          .custom-delete-modal .modal-body {
            background-color: #1a1a1a;
            padding: 24px;
            color: #fff;
          }
          .custom-delete-modal .modal-footer {
            background-color: #2c2c2c;
            border-top: 1px solid #444;
            padding: 16px 24px;
            border-radius: 0 0 12px 12px;
            justify-content: flex-end;
            gap: 12px;
          }
        `}</style>
        <Modal.Header closeButton>
          <Modal.Title style={{ color: '#fff', fontSize: '16px', fontWeight: '600' }}>
            {window.location.hostname} says
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ 
            margin: 0, 
            fontSize: '15px', 
            lineHeight: '1.6',
            color: '#e0e0e0',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}>
            Are you sure you want to delete this {testToDelete?.serviceName || 'test'}? This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            onClick={() => {
              setShowDeleteModal(false);
              setTestToDelete(null);
            }}
            className="custom-cancel-btn"
            style={{
              backgroundColor: '#6b46c1',
              border: 'none',
              color: '#fff',
              padding: '10px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              textDecoration: 'none',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#7c3aed';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#6b46c1';
            }}
          >
            Cancel
          </Button>
          <Button
            variant="link"
            onClick={confirmDelete}
            className="custom-ok-btn"
            style={{
              backgroundColor: '#a78bfa',
              border: '2px solid #c4b5fd',
              color: '#fff',
              padding: '10px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              boxShadow: '0 0 0 1px rgba(196, 181, 253, 0.4)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#c4b5fd';
              e.target.style.borderColor = '#ddd6fe';
              e.target.style.boxShadow = '0 0 0 2px rgba(196, 181, 253, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#a78bfa';
              e.target.style.borderColor = '#c4b5fd';
              e.target.style.boxShadow = '0 0 0 1px rgba(196, 181, 253, 0.4)';
            }}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OtherServicesDashboard;
